import {
  AcknowledgeBackgroundJobMutation,
  AcknowledgeBackgroundJobMutationVariables,
  BackgroundJobQuery,
  BackgroundJobQueryVariables,
  BackgroundJobStatus,
  CancelBackgroundJobMutation,
  CancelBackgroundJobMutationVariables,
  InitiateBulkJobMutation,
  InitiateBulkJobMutationVariables,
  PendingBackgroundJobsForUserQuery,
} from 'types/graphql'

import { QueryKey, useMutation, useQuery } from 'src/utils/queryClient'

import { isActiveBackgroundJobStatus } from './backgroundJobStatus'
import { BackgroundJobProgressFragment } from './fragments'

export const BACKGROUND_JOB_POLL_INTERVAL_MS = 2_000

/** Shared by pending-jobs polling and single-job queries. */
export const getBackgroundJobPollIntervalMs = (
  status: BackgroundJobStatus | null | undefined
): number | false => {
  if (status && isActiveBackgroundJobStatus(status)) {
    return BACKGROUND_JOB_POLL_INTERVAL_MS
  }
  return false
}

export const getPendingBackgroundJobsPollIntervalMs = (
  backgroundJobs: { status: BackgroundJobStatus }[] | null | undefined
): number | false => {
  if (!backgroundJobs?.length) {
    return false
  }
  const hasActiveBackgroundJob = backgroundJobs.some((backgroundJob) =>
    isActiveBackgroundJobStatus(backgroundJob.status)
  )
  return hasActiveBackgroundJob ? BACKGROUND_JOB_POLL_INTERVAL_MS : false
}

const BackgroundJobQueryDocument = gql`
  query BackgroundJobQuery($id: Int!) {
    backgroundJob(id: $id) {
      ...BackgroundJobProgressFragment
    }
  }
  ${BackgroundJobProgressFragment}
`

export { BackgroundJobQueryDocument }

export const useBackgroundJobQuery = ({
  id,
  enabled = true,
}: {
  id: number | null | undefined
  enabled?: boolean
}) =>
  useQuery<BackgroundJobQuery, BackgroundJobQueryVariables>({
    queryKey: [QueryKey.BackgroundJob, id],
    queryDocument: BackgroundJobQueryDocument,
    variables: { id: id ?? -1 },
    enabled: enabled && id != null,
    refetchInterval: (query) =>
      getBackgroundJobPollIntervalMs(query.state.data?.backgroundJob?.status),
  })

const PendingBackgroundJobsForUserQueryDocument = gql`
  query PendingBackgroundJobsForUserQuery {
    pendingBackgroundJobsForUser {
      ...BackgroundJobProgressFragment
    }
  }
  ${BackgroundJobProgressFragment}
`

export const usePendingBackgroundJobsForUserQuery = ({
  organizationId,
  enabled = true,
}: {
  organizationId: number | undefined
  enabled?: boolean
}) =>
  useQuery<PendingBackgroundJobsForUserQuery>({
    queryKey: [QueryKey.PendingBackgroundJobsForUser, organizationId],
    queryDocument: PendingBackgroundJobsForUserQueryDocument,
    enabled: enabled && organizationId != null,
    refetchInterval: (query) =>
      getPendingBackgroundJobsPollIntervalMs(query.state.data?.pendingBackgroundJobsForUser),
  })

const InitiateBulkJobMutationDocument = gql`
  mutation InitiateBulkJobMutation($input: InitiateBulkJobInput!) {
    initiateBulkJob(input: $input) {
      ...BackgroundJobProgressFragment
    }
  }
  ${BackgroundJobProgressFragment}
`

export const useInitiateBulkJobMutation = () =>
  useMutation<InitiateBulkJobMutation, InitiateBulkJobMutationVariables>({
    mutationDocument: InitiateBulkJobMutationDocument,
  })

const CancelBackgroundJobMutationDocument = gql`
  mutation CancelBackgroundJobMutation($id: Int!) {
    cancelBackgroundJob(id: $id) {
      ...BackgroundJobProgressFragment
    }
  }
  ${BackgroundJobProgressFragment}
`

export const useCancelBackgroundJobMutation = () =>
  useMutation<CancelBackgroundJobMutation, CancelBackgroundJobMutationVariables>({
    mutationDocument: CancelBackgroundJobMutationDocument,
  })

const AcknowledgeBackgroundJobMutationDocument = gql`
  mutation AcknowledgeBackgroundJobMutation($input: AcknowledgeBackgroundJobInput!) {
    acknowledgeBackgroundJob(input: $input) {
      ...BackgroundJobProgressFragment
    }
  }
  ${BackgroundJobProgressFragment}
`

export const useAcknowledgeBackgroundJobMutation = () =>
  useMutation<AcknowledgeBackgroundJobMutation, AcknowledgeBackgroundJobMutationVariables>({
    mutationDocument: AcknowledgeBackgroundJobMutationDocument,
  })
